import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'

import { StatusTag } from '../../../../../../components/tableTemplates'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import {
  useDespatchAdviceById,
  useDespatchAdviceItemById,
} from '../../../../data'

interface Props {
  visible: boolean
  setVisible: (params: boolean) => void
  id: string
  itemId: string
}

export const Info: React.FC<Props> = ({ visible, setVisible, id, itemId }) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const { uiConfig: config } = useConfigContext()

  const { data: despatchAdvice } = useDespatchAdviceById(id)
  const { data: despatchAdviceItem, isLoading } = useDespatchAdviceItemById(
    id,
    itemId!,
  )

  const currency = despatchAdvice?.Payload.OrderCurrency

  // ************************************************************
  // components
  // ************************************************************
  // itemId
  const itemIdLabel = <label htmlFor="itemId" className="text-sm"></label>

  const itemIdComponent = (
    <InputText
      id="itemId"
      className="w-full"
      value={despatchAdviceItem?.ItemId}
      disabled
    />
  )

  const itemIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={itemIdComponent}
      label={itemIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationItemId
  const confirmationItemIdLabel = (
    <label htmlFor="confirmationItemId" className="text-sm"></label>
  )

  const confirmationItemIdComponent = (
    <InputText
      id="confirmationItemId"
      value={despatchAdviceItem?.ConfirmationItemId}
      className="w-full"
      disabled
    />
  )

  const confirmationItemIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationItemIdComponent}
      label={confirmationItemIdLabel}
      isLoading={isLoading}
    />
  )

  // countryOfOrigin
  const countryOfOriginLabel = (
    <label htmlFor="countryOfOrigin" className="text-sm"></label>
  )

  const countryOfOriginComponent = (
    <InputText
      id="countryOfOrigin"
      className="w-full"
      value={despatchAdviceItem?.CountryOfOrigin}
      disabled
    />
  )

  const countryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={countryOfOriginComponent}
      label={countryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // confirmationCountryOfOrigin
  const confirmationCountryOfOriginLabel = (
    <label htmlFor="confirmationCountryOfOrigin" className="text-sm"></label>
  )

  const confirmationCountryOfOriginComponent = (
    <InputText
      id="confirmationCountryOfOrigin"
      value={despatchAdviceItem?.ConfirmationCountryOfOrigin}
      className="w-full"
      disabled
    />
  )

  const confirmationCountryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationCountryOfOriginComponent}
      label={confirmationCountryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // gtin
  const gtinLabel = <label htmlFor="gtin" className="text-sm"></label>

  const gtinComponent = (
    <InputText
      id="gtin"
      className="w-full"
      value={despatchAdviceItem?.GTIN}
      disabled
    />
  )

  const gtinWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={gtinComponent}
      label={gtinLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={despatchAdviceItem?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductId
  const confirmationSellerProductIdLabel = (
    <label htmlFor="confirmationSellerProductId" className="text-sm"></label>
  )

  const confirmationSellerProductIdComponent = (
    <InputText
      id="confirmationSellerProductId"
      value={despatchAdviceItem?.ConfirmationSellerProductId}
      className="w-full"
      disabled
    />
  )

  const confirmationSellerProductIdWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationSellerProductIdComponent}
      label={confirmationSellerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={despatchAdviceItem?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductDescription
  const confirmationSellerProductDescriptionLabel = (
    <label
      htmlFor="confirmationSellerProductDescription"
      className="text-sm"
    ></label>
  )

  const confirmationSellerProductDescriptionComponent = (
    <InputText
      id="confirmationSellerProductDescription"
      value={despatchAdviceItem?.ConfirmationSellerProductDescription}
      className="w-full"
      disabled
    />
  )

  const confirmationSellerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationSellerProductDescriptionComponent}
      label={confirmationSellerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  //  quantity
  const quantityLabel = <label htmlFor="quantity" className="text-sm"></label>

  const quantityComponent = (
    <InputNumber
      id="quantity"
      className="w-full"
      value={despatchAdviceItem?.Quantity}
      locale={language}
      disabled
    />
  )

  const quantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={quantityComponent}
      label={quantityLabel}
      isLoading={isLoading}
    />
  )

  // openQuantity
  function getOpenQuantity() {
    const openQuantity = despatchAdviceItem?.OpenQuantity ?? 0
    const confirmedQuantity = despatchAdviceItem?.ConfirmationQuantity ?? 0

    return openQuantity - confirmedQuantity
  }

  const openQuantityLabel = (
    <label htmlFor="openQuantity" className="text-sm"></label>
  )

  const openQuantityComponent = (
    <InputNumber
      id="openQuantity"
      className="w-full"
      value={despatchAdviceItem?.OpenQuantity}
      locale={language}
      disabled
    />
  )

  const openQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={openQuantityComponent}
      label={openQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationQuantity
  const confirmationQuantityLabel = (
    <label htmlFor="confirmationQuantity" className="text-sm"></label>
  )

  const confirmationQuantityComponent = (
    <InputNumber
      id="confirmationQuantity"
      value={despatchAdviceItem?.ConfirmationQuantity}
      className="w-full"
      min={0}
      max={despatchAdviceItem?.Quantity}
      disabled
      locale={language}
      aria-describedby="open-quantity"
    />
  )

  const confirmationQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationQuantityComponent}
      label={confirmationQuantityLabel}
      className="mb-1"
      isLoading={isLoading}
    />
  )

  // deliveryStatus
  const deliveryStatusLabel = (
    <label htmlFor="deliveryStatus" className="text-sm"></label>
  )

  const deliveryStatusComponent = (
    <Dropdown
      id="deliveryStatus"
      className="w-full"
      value={despatchAdviceItem?.DeliveryStatus}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const deliveryStatusWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={deliveryStatusComponent}
      label={deliveryStatusLabel}
      isLoading={isLoading}
    />
  )
  // unit
  const unitLabel = <label htmlFor="unit" className="text-sm"></label>

  const unitComponent = (
    <InputText
      id="unit"
      className="w-full"
      value={despatchAdviceItem?.Unit}
      disabled
    />
  )

  const unitWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={unitComponent}
      label={unitLabel}
      isLoading={isLoading}
    />
  )

  //  confirmationUnit
  const confirmationUnitLabel = (
    <label htmlFor="confirmationUnit" className="text-sm"></label>
  )

  const confirmationUnitComponent = (
    <InputText
      id="confirmationUnit"
      value={despatchAdviceItem?.ConfirmationUnit}
      className="w-full"
      disabled
    />
  )

  const confirmationUnitWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationUnitComponent}
      label={confirmationUnitLabel}
      isLoading={isLoading}
    />
  )

  // priceBaseQuantity
  const priceBaseQuantityLabel = (
    <label htmlFor="priceBaseQuantity" className="text-sm"></label>
  )

  const priceBaseQuantityComponent = (
    <InputNumber
      id="priceBaseQuantity"
      className="w-full"
      value={despatchAdviceItem?.PriceBaseQuantity}
      locale={language}
      disabled
    />
  )

  const priceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={priceBaseQuantityComponent}
      label={priceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceBaseQuantity
  const confirmationPriceBaseQuantityLabel = (
    <label htmlFor="confirmationPriceBaseQuantity" className="text-sm"></label>
  )

  const confirmationPriceBaseQuantityComponent = (
    <InputNumber
      id="confirmationPriceBaseQuantity"
      value={despatchAdviceItem?.ConfirmationPriceBaseQuantity}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationPriceBaseQuantityComponent}
      label={confirmationPriceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )
  // priceUnitOfMeasure
  const priceUnitOfMeasureLabel = (
    <label htmlFor="priceUnitOfMeasure" className="text-sm"></label>
  )

  const priceUnitOfMeasureComponent = (
    <InputText
      id="priceUnitOfMeasure"
      className="w-full"
      value={despatchAdviceItem?.PriceUnitOfMeasure}
      disabled
    />
  )

  const priceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={priceUnitOfMeasureComponent}
      label={priceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceUnitOfMeasure
  const confirmationPriceUnitOfMeasureLabel = (
    <label htmlFor="confirmationPriceUnitOfMeasure" className="text-sm"></label>
  )

  const confirmationPriceUnitOfMeasureComponent = (
    <InputText
      id="confirmationPriceUnitOfMeasure"
      value={despatchAdviceItem?.ConfirmationPriceUnitOfMeasure?.toUpperCase()}
      className="w-full"
      disabled
    />
  )

  const confirmationPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationPriceUnitOfMeasureComponent}
      label={confirmationPriceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // price
  const priceLabel = <label htmlFor="price" className="text-sm"></label>

  const priceComponent = (
    <InputNumber
      id="price"
      value={despatchAdviceItem?.Price}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={priceComponent}
      label={priceLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPrice
  const confirmationPriceLabel = (
    <label htmlFor="confirmationPrice" className="text-sm"></label>
  )

  const confirmationPriceComponent = (
    <InputNumber
      id="confirmationPrice"
      value={despatchAdviceItem?.ConfirmationPrice}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationPriceComponent}
      label={confirmationPriceLabel}
      isLoading={isLoading}
    />
  )

  // priceSum
  const priceSumLabel = <label htmlFor="priceSum" className="text-sm"></label>

  const priceSumComponent = (
    <InputNumber
      id="priceSum"
      value={despatchAdviceItem?.PriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const priceSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={priceSumComponent}
      label={priceSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceSum
  const confirmationPriceSumLabel = (
    <label htmlFor="confirmationPriceSum" className="text-sm"></label>
  )

  const confirmationPriceSumComponent = (
    <InputNumber
      id="confirmationPriceSum"
      value={despatchAdviceItem?.ConfirmationPriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationPriceSumComponent}
      label={confirmationPriceSumLabel}
      isLoading={isLoading}
    />
  )

  // allowanceChargeSum
  const allowanceChargeSumLabel = (
    <label htmlFor="allowanceChargeSum" className="text-sm"></label>
  )

  const allowanceChargeSumComponent = (
    <InputNumber
      id="allowanceChargeSum"
      value={despatchAdviceItem?.AllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const allowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={allowanceChargeSumComponent}
      label={allowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAllowanceChargeSum
  const confirmationAllowanceChargeSumLabel = (
    <label htmlFor="confirmationAllowanceChargeSum" className="text-sm"></label>
  )

  const confirmationAllowanceChargeSumComponent = (
    <InputNumber
      id="confirmationAllowanceChargeSum"
      value={despatchAdviceItem?.ConfirmationAllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationAllowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationAllowanceChargeSumComponent}
      label={confirmationAllowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // lineItemAmount
  const lineItemAmountLabel = (
    <label htmlFor="lineItemAmount" className="text-sm"></label>
  )

  const lineItemAmountComponent = (
    <InputNumber
      id="lineItemAmount"
      value={despatchAdviceItem?.LineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const lineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={lineItemAmountComponent}
      label={lineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationLineItemAmount
  const confirmationLineItemAmountLabel = (
    <label htmlFor="confirmationLineItemAmount" className="text-sm"></label>
  )

  const confirmationLineItemAmountComponent = (
    <InputNumber
      id="confirmationLineItemAmount"
      value={despatchAdviceItem?.ConfirmationLineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationLineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationLineItemAmountComponent}
      label={confirmationLineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // deliveryDate
  const deliveryDateLabel = (
    <label htmlFor="deliveryDate" className="text-sm"></label>
  )

  const deliveryDate = despatchAdviceItem?.DeliveryDate
    ? new Date(despatchAdviceItem?.DeliveryDate)
    : null

  const deliveryDateComponent = (
    <Calendar
      id="deliveryDate"
      value={deliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label htmlFor="confirmationDeliveryDate" className="text-sm"></label>
  )

  const confirmationDeliveryDate = despatchAdviceItem?.ConfirmationDeliveryDate
    ? new Date(despatchAdviceItem?.ConfirmationDeliveryDate)
    : null

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={confirmationDeliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationReasonCode
  const confirmationReasonCodeLabel = (
    <label htmlFor="confirmationReasonCode" className="text-sm"></label>
  )

  const confirmationReasonCodeComponent = (
    <Dropdown
      id="confirmationReasonCode"
      className="w-full"
      value={despatchAdviceItem?.ConfirmationReason?.Code}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const confirmationReasonCodeWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationReasonCodeComponent}
      label={confirmationReasonCodeLabel}
      isLoading={isLoading}
    />
  )

  // confirmationReasonDescription
  const confirmationReasonDescriptionLabel = (
    <label htmlFor="confirmationReasonDescription" className="text-sm"></label>
  )

  const confirmationReasonDescriptionComponent = (
    <InputText
      id="confirmationReasonDescription"
      value={despatchAdviceItem?.ConfirmationReason?.Description}
      className="w-full"
      disabled
    />
  )

  const ConfirmationReasonDescriptionWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={confirmationReasonDescriptionComponent}
      label={confirmationReasonDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // notes
  const notesLabel = <label htmlFor="notes" className="text-sm"></label>

  const notesComponent = (
    <InputTextarea
      id="notes"
      rows={5}
      value={despatchAdviceItem?.Notes}
      className="w-full"
      disabled
    />
  )

  const notesWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.infoDespatchAdviceItem}
      component={notesComponent}
      label={notesLabel}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************

  // header
  const header = (
    <div className="flex align-content-center flex-wrap">
      <span className="mr-3">
        {t('common.titles.infoItem', {
          position: despatchAdviceItem?.OrderPosition,
        })}
      </span>

      <StatusTag status={despatchAdviceItem?.ConfirmationCode} />
    </div>
  )

  // footer
  const footer = (
    <Button
      label={t('common.buttons.back')}
      icon="pi pi-check"
      onClick={() => setVisible(false)}
      autoFocus
      className="mt-3 w-full md:w-auto"
    />
  )

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={() => setVisible(false)}
      blockScroll
    >
      {/* itemId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{itemIdWrapper}</div>
        <div className="col-12 md:col-4">{confirmationItemIdWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* countryOfOrigin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{countryOfOriginWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationCountryOfOriginWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* gtin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{gtinWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{buyerProductIdWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationSellerProductIdWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductDescription */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{buyerProductDescriptionWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationSellerProductDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* quantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{quantityWrapper}</div>
      </div>

      {/* openQuantity, confirmationQuantity, deliveryStatus  */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{openQuantityWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationQuantityWrapper}
          {/* only show this help text if the open quantity is bigger than 0 */}
          {getOpenQuantity() > 0 && (
            <small id="open-quantity" className="block mb-3">
              {t(
                'despatchAdvice.positions.table.info.dialog.form.confirmationQuantity.help.openQuantity',
                {
                  openQuantity: getOpenQuantity().toLocaleString(language),
                },
              )}
            </small>
          )}
        </div>
        {/* only show this field if the open quantity is bigger than 0 */}
        {getOpenQuantity() > 0 && (
          <div className="col-12 md:col-4">{deliveryStatusWrapper}</div>
        )}
      </div>

      <Divider className="md:hidden" />

      {/* unit */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{unitWrapper}</div>
        <div className="col-12 md:col-4">{confirmationUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* priceBaseQuantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceBaseQuantityWrapper}</div>

        <div className="col-12 md:col-4">
          {confirmationPriceBaseQuantityWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* priceUnitOfMeasure */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceUnitOfMeasureWrapper}</div>
        <div className="col-12 md:col-4">
          {confirmationPriceUnitOfMeasureWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* price */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceWrapper}</div>

        <div className="col-12 md:col-4">{confirmationPriceWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* priceSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{priceSumWrapper}</div>

        <div className="col-12 md:col-4">{confirmationPriceSumWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* allowanceChargeSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{allowanceChargeSumWrapper}</div>

        <div className="col-12 md:col-4">
          {confirmationAllowanceChargeSumWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* ItemAmount */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{lineItemAmountWrapper}</div>

        <div className="col-12 md:col-4">
          {confirmationLineItemAmountWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* deliveryDate */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{deliveryDateWrapper}</div>

        <div className="col-12 md:col-4">{confirmationDeliveryDateWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* confirmationCode */}
      <div className="formgrid grid">
        <div className="col-12 md:col-4">{confirmationReasonCodeWrapper}</div>
        <div className="col-12 md:col-4">
          {ConfirmationReasonDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* notes */}
      <div className="formgrid grid">
        <div className="col-12">{notesWrapper}</div>
      </div>
    </Dialog>
  )
}
